import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { OfferData, OfferFull, OfferStatus } from '@recruitee/http/types';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';

import { EntityStoreKey, OfferScope, UpdateSocialMediaActionPayload } from '../../types';
import { StatusChangeType } from './../../types';

const {
  actions: defaultActions,
  namespace,
  selectors,
  reducer,
} = createEntityCollectionStore<OfferFull>()(EntityStoreKey.JobsFull, {
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => ({
    fetchOne: (state, payload: { id: number | string; langCode?: string }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    fetchOneSuccess: (state, payload: { id: number | string; offer: OfferFull }) =>
      pipeState(
        state,
        next => adapter.upsertOne(next, payload.offer),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    fetchOneFailed: (state, payload: { id: number | string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    updateOne: (state, payload: OfferFull) => pipeState(adapter.updateOne(state, payload)),
    follow: (state, payload: { id: number; scope: OfferScope }) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    followSuccess: (state, payload: { job: OfferFull; scope: OfferScope }) =>
      pipeState(adapter.upsertOne(state, payload.job), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.job.id] }),
      ),
    followFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    unfollow: (state, payload: { id: number; scope: OfferScope }) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    unfollowSuccess: (state, payload: { job: OfferFull; scope: OfferScope }) =>
      pipeState(adapter.upsertOne(state, payload.job), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.job.id] }),
      ),
    unfollowFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    duplicate: ngrxEvent<{ id: number }>(),
    duplicateSuccess: (state, payload: { offer: OfferFull }) =>
      adapter.upsertOne(state, payload.offer),
    duplicateFailed: ngrxEvent<{ id: number; error: HttpErrorResponse & { errorFields: any } }>(),
    create: ngrxEvent<{ offer: OfferData }>(),
    createSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),
    createFromRequisition: ngrxEvent<{ requisitionId: number }>(),
    createFromRequisitionSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFromRequisitionFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),
    update: (state, payload: { offer: OfferData & { id: number } }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offer.id] })),
    updateSuccess: (state, payload: { id: number; data: OfferFull }) =>
      pipeState(adapter.updateOne(state, payload.data), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    updateFailed: (
      state,
      payload: { id: number; error: HttpErrorResponse & { errorFields: any } },
    ) => pipeState(adapter.setError(state, payload.error, { scope: null, entities: [payload.id] })),
    changeStatus: (
      state,
      payload: {
        id: number;
        status: StatusChangeType;
        currentStatus: OfferStatus;
      },
    ) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    changeStatusSuccess: (
      state,
      payload: { offer: OfferFull; targetScope: OfferScope; isChangingScope: boolean },
    ) =>
      pipeState(adapter.upsertOne(state, payload.offer), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.offer.id] }),
      ),
    changeStatusFailed: (
      state,
      payload: { error: HttpErrorResponse & { errorFields: any }; id: number },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    preview: ngrxEvent<{ offer: OfferData }>(),
    previewSuccess: ngrxEvent<{ previewUrl: string }>(),
    previewFailed: ngrxEvent<{
      error: HttpErrorResponse;
    }>(),
    updateSocialMedia: (state, payload: UpdateSocialMediaActionPayload) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    updateSocialMediaSuccess: (state, payload: { offer: OfferFull }) =>
      pipeState(
        state,
        next => adapter.updateOne(next, payload.offer),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.offer.id] }),
      ),
    updateSocialMediaFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    updateSocialMediaImage: (state, payload: { id: number; file: File }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    updateSocialMediaImageSuccess: (state, payload: { id: number; url: string }) => {
      const updatedOffer = <OfferFull>{ ...state.entityMap[payload.id], sharingImage: payload.url };
      return pipeState(
        state,
        next => adapter.updateOne(next, updatedOffer),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      );
    },
    updateSocialMediaImageFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    updateCoverImage: (state, payload: { id: number; file: File }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    updateCoverImageSuccess: (state, payload: { id: number; url: string }) => {
      const updatedOffer = <OfferFull>{
        ...state.entityMap[payload.id],
        coverImageUrl: payload.url,
      };
      return pipeState(
        state,
        next => adapter.updateOne(next, updatedOffer),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      );
    },
    updateCoverImageFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
  }),
});

const bySlug = memoizeSelector((slug: string) =>
  createSelector(selectors.entities({ scope: null }), (state: OfferFull[]) =>
    state.find(offer => offer.slug === slug),
  ),
);

const actions = {
  changeStatus: defaultActions.changeStatus,
  changeStatusFailed: defaultActions.changeStatusFailed,
  changeStatusSuccess: defaultActions.changeStatusSuccess,
  create: defaultActions.create,
  createFailed: defaultActions.createFailed,
  createSuccess: defaultActions.createSuccess,
  createFromRequisition: defaultActions.createFromRequisition,
  createFromRequisitionFailed: defaultActions.createFromRequisitionFailed,
  createFromRequisitionSuccess: defaultActions.createFromRequisitionSuccess,
  duplicate: defaultActions.duplicate,
  duplicateFailed: defaultActions.duplicateFailed,
  duplicateSuccess: defaultActions.duplicateSuccess,
  fetchOne: defaultActions.fetchOne,
  fetchOneFailed: defaultActions.fetchOneFailed,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  follow: defaultActions.follow,
  followFailed: defaultActions.followFailed,
  followSuccess: defaultActions.followSuccess,
  unfollow: defaultActions.unfollow,
  unfollowFailed: defaultActions.unfollowFailed,
  unfollowSuccess: defaultActions.unfollowSuccess,
  update: defaultActions.update,
  updateFailed: defaultActions.updateFailed,
  updateOne: defaultActions.updateOne,
  updateSuccess: defaultActions.updateSuccess,
  removeOne: defaultActions.removeOne,
  preview: defaultActions.preview,
  previewSuccess: defaultActions.previewSuccess,
  previewFailed: defaultActions.previewFailed,
  updateSocialMedia: defaultActions.updateSocialMedia,
  updateSocialMediaSuccess: defaultActions.updateSocialMediaSuccess,
  updateSocialMediaFailed: defaultActions.updateSocialMediaFailed,
  updateSocialMediaImage: defaultActions.updateSocialMediaImage,
  updateSocialMediaImageSuccess: defaultActions.updateSocialMediaImageSuccess,
  updateSocialMediaImageFailed: defaultActions.updateSocialMediaImageFailed,
  updateCoverImage: defaultActions.updateCoverImage,
  updateCoverImageSuccess: defaultActions.updateCoverImageSuccess,
  updateCoverImageFailed: defaultActions.updateCoverImageFailed,
};

const JobsFullSelectors = {
  ...selectors,
  bySlug,
};

export {
  actions as JobsFullActions,
  JobsFullSelectors,
  namespace as JobsFullNamespace,
  reducer as JobsFullReducer,
};
